/**
 * ============================================================================
 * api/client.ts — 본 시스템 자체 axios 인스턴스 (legacy, framework/api/axios 와 공존)
 * ============================================================================
 *
 * 【역할】
 *   백엔드 FastAPI(http://localhost:37086) 호출용 axios 인스턴스. 초기에
 *   사용되었으며, 현재는 대부분 framework/api/axios 로 이관됨. 그러나 일부
 *   레거시 화면(LoginPage 등)에서 본 인스턴스를 직접 import 하므로 유지.
 *
 * 【주요 기능】
 *   - 요청 인터셉터: localStorage 의 fw.accessToken (또는 accessToken)을 Bearer
 *     헤더로 자동 부착
 *   - 응답 인터셉터: 401 발생 시 1회 한정 refresh 토큰으로 재발급 시도 후
 *     원 요청 재실행 (refresh 동시 호출 방지를 위한 isRefreshing 플래그)
 *   - 로그인 엔드포인트(/api/v1/auth/login)의 401 은 refresh 시도 X (잘못된
 *     비밀번호 케이스 — 즉시 호출자에게 reject 전달)
 *
 * 【포팅 원본】 vue-spring-fw `api/axios.ts` (Pinia → fw.* localStorage 키 동등)
 * 【의존성】 axios, env: VITE_API_BASE_URL
 */
import axios from 'axios';

/**
 * 백엔드 base URL — 상대경로(same-origin) 고정.
 * 호출부는 모두 `/api/v1/...` 전체 경로를 사용하므로 baseURL 은 비워 둔다.
 * 로컬=Vite dev proxy(`/api`→백엔드), 배포=Cloudflare Pages Function(`/api/*`→BACKEND_URL)이
 * 백엔드로 전달한다. 절대/터널 URL 을 소스에 하드코딩하지 않는다(환경별 프록시만 다름).
 */
const baseURL = '';

/** axios 공유 인스턴스 — JSON 컨텐츠 + 토큰 인터셉터 부착됨. */
export const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

// ─── 요청 인터셉터: 토큰 자동 부착 ─
api.interceptors.request.use((config) => {
  // framework/api/axios 와 동일한 키 사용 (fw.accessToken / fw.refreshToken)
  // 레거시 호환: 구 키 (accessToken)도 fallback 으로 인정
  const token = localStorage.getItem('fw.accessToken') || localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** refresh 요청 동시 호출 방지 플래그 (모듈 스코프 단일 변수). */
let isRefreshing = false;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const url = (original?.url || '') as string;
    // 로그인 엔드포인트의 401 은 refresh 시도 X (잘못된 비밀번호 처리)
    const isLoginCall = url.includes('/api/v1/auth/login');
    if (error.response?.status === 401 && !isLoginCall && !original._retry && !isRefreshing) {
      original._retry = true;
      isRefreshing = true;
      try {
        const refresh = localStorage.getItem('fw.refreshToken') || localStorage.getItem('refreshToken');
        if (!refresh) throw error;
        const { data } = await axios.post(`${baseURL}/api/v1/auth/refresh`, { refreshToken: refresh });
        if (data?.success) {
          localStorage.setItem('fw.accessToken', data.data.accessToken);
          original.headers.Authorization = `Bearer ${data.data.accessToken}`;
          return api(original);
        }
      } catch {
        localStorage.removeItem('fw.accessToken');
        localStorage.removeItem('fw.refreshToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // 페이지 강제 reload 제거 — 호출자가 catch로 받아 처리
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);
